import { Flashbar, Select, Spinner } from '@amzn/awsui-components-react';
import PageHeader, { PageHeaderButton } from '../PageHeader';
import { Poll, PollSchedule, ServerPoll } from '../../data-types';
import { available_locale_options, getSelection } from '../../configs/multi-select-config';
import { fetchCreatePoll, fetchDeletePoll, fetchGetPoll, fetchQueryPoll, fetchUpdatePoll } from '../../utils/fetchUtil';
import { itemError, itemSuccess } from '../commons/flash-messages';

import FanPollDataTab from './FanPollData';
import FanPollModal from './FanPollModal';
import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { applyChange } from '../../utils/eventUtil';
import { deepCopy } from '../../utils/jsonUtil';
import { pollValidation } from '../../validation/pollValidation';

// Constants
const SURFACE_TYPES = {
    ASIN: 'ASIN',
    EVENT: 'EVENT',
} as const;

const SUFFIXES = {
    ASIN: '#ASIN',
    EVENT: '#EVENT',
} as const;

const CUSTOM_EVENTS = {
    CLEAR_FLASHBAR: 'clearFlashbar',
    UPDATE_SURFACE: 'updateSurface',
} as const;

// Types
interface State {
    selectedLocale: string;
    selectedSurface: string;
    polls?: ServerPoll[];
    poll: Poll;
    activeTabId?: string;
    isEdit: boolean;
    loading: boolean;
    saving: boolean;
    deleting: boolean;
    flashbar: Flashbar.MessageDefinition[];
    showDeletePopup: boolean;
    showSavePopup: boolean;
    pollLocales: string[];
}

interface RouteParams {
    pollId: string;
}

type Props = RouteComponentProps<RouteParams>;

// Utility functions
const getLocaleOptions = (options: Select.Option[], locales?: string[]): Select.Option[] => {
    if (!locales) return [];
    return options.filter((option) => locales.includes(option.id));
};

const determineSurfaceType = (association?: string): string => {
    if (!association) return '';
    if (association.includes(SUFFIXES.EVENT)) return SURFACE_TYPES.EVENT;
    if (association.includes(SUFFIXES.ASIN)) return SURFACE_TYPES.ASIN;
    return '';
};

class EditPoll extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedLocale: '',
            selectedSurface: '',
            polls: undefined,
            poll: { association: '' },
            isEdit: true,
            loading: false,
            saving: false,
            deleting: false,
            flashbar: [],
            showDeletePopup: false,
            showSavePopup: false,
            pollLocales: [],
        };
    }

    componentDidMount(): void {
        this.initializeComponent();
    }

    componentDidUpdate(prevProps: Props): void {
        this.updateBrowserHistory();
        if (this.shouldReloadPoll(prevProps)) {
            this.handlePollNavigation();
        }
    }

    // Initialization methods
    private initializeComponent(): void {
        const locale = this.getLocaleFromQuery();
        this.setState({ selectedLocale: locale });
        this.loadPollDetails(locale).catch(this.handleLoadError);

        if (this.state.isEdit) {
            this.setState({ flashbar: [] });
            this.loadPollDetails(locale);
        }
    }

    private getLocaleFromQuery(): string {
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get('locale') ?? '';
    }

    private handleLoadError = (error: any): void => {
        this.setState({ flashbar: [itemError('Failed to load poll', error)] });
    };

    // Navigation and history methods
    private updateBrowserHistory(): void {
        const currentURL = new URL(window.location.href);
        const searchParams = new URLSearchParams(currentURL.search);
        searchParams.set('locale', this.state.selectedLocale);
        const newURL = `${currentURL.origin}${currentURL.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', newURL);
    }

    private shouldReloadPoll(prevProps: Props): boolean {
        return prevProps.match?.params?.pollId !== this.props.match?.params?.pollId;
    }

    private handlePollNavigation(): void {
        this.setState({ flashbar: [], activeTabId: 'data' });
        this.loadPollDetails(this.state.selectedLocale);
        this.setState({ pollLocales: this.buildPollLocales(this.state.polls) });
    }

    // Data loading methods
    private buildPollLocales = (polls?: ServerPoll[]): string[] => {
        return polls ? polls.map((poll) => poll.locale) : [];
    };

    private async loadPollDetails(locale: string): Promise<void> {
        const pollId = this.props.match.params.pollId;

        if (pollId === 'create') {
            this.initializeNewPoll();
            return;
        }

        await this.loadExistingPoll(pollId, locale);
    }

    private initializeNewPoll(): void {
        this.setState({
            poll: { association: '' },
            selectedLocale: '',
            selectedSurface: '',
            isEdit: false,
            pollLocales: [],
        });
    }

    private async loadExistingPoll(pollId: string, locale: string): Promise<void> {
        this.setState({ isEdit: true, loading: true });

        if (!this.state.polls) {
            this.fetchLocales(pollId);
        }

        try {
            const poll = await fetchGetPoll(pollId, locale);
            const surfaceType = determineSurfaceType(poll.association);
            this.setState({ poll, selectedSurface: surfaceType });
        } catch (error) {
            this.setState({ poll: {}, flashbar: [itemError('Failed to load poll', error)] });
        } finally {
            this.setState({ loading: false });
        }
    }

    private async fetchLocales(pollId: string): Promise<void> {
        try {
            const polls = await fetchQueryPoll(pollId);
            const locales = polls.map((poll) => poll.locale || '').filter((locale) => locale !== '');
            this.setState({
                polls: deepCopy(polls),
                pollLocales: locales,
            });
        } catch (error) {
            this.setState({
                poll: {},
                flashbar: [itemError('Failed to load poll', error)],
            });
        }
    }

    // Validation methods
    private validatePollFields(action: 'creating' | 'saving' = 'creating'): boolean {
        if (!this.state.selectedSurface) {
            this.setState({
                flashbar: [itemError(`Please select a Surface Type before ${action} the poll`, '')],
                showSavePopup: false,
            });
            return false;
        }
        return true;
    }

    // Event handlers
    private handleLocaleChange = (event: CustomEvent): void => {
        this.setState({ selectedLocale: event.detail.selectedOption.id });
        this.loadPollDetails(event.detail.selectedOption.id);
    };

    private handleChange = (e: CustomEvent): void => {
        if (this.handleCustomEvents(e)) return;

        const poll = deepCopy(this.state.poll);
        this.handleFieldChanges(e);
        applyChange(e, poll);
        this.setState({ poll });
    };

    private handleCustomEvents(e: CustomEvent): boolean {
        switch (e.type) {
            case CUSTOM_EVENTS.CLEAR_FLASHBAR:
                this.setState({ flashbar: [] });
                return true;
            case CUSTOM_EVENTS.UPDATE_SURFACE:
                this.handleSurfaceUpdate(e.detail?.surfaceType);
                return true;
            default:
                return false;
        }
    }

    private handleSurfaceUpdate(surfaceType: string): void {
        if (surfaceType) {
            this.setState({ selectedSurface: surfaceType, flashbar: [] });
        }
    }

    private handleFieldChanges(e: CustomEvent): void {
        const { id } = e.detail || {};

        switch (id) {
            case 'surfaceType':
                this.handleSurfaceTypeChange(e);
                break;
            case 'association':
                this.handleAssociationChange(e);
                break;
        }
    }

    private handleSurfaceTypeChange(e: CustomEvent): void {
        const surfaceType = e.detail?.selectedOption?.id;
        if (surfaceType) {
            this.setState({ selectedSurface: surfaceType, flashbar: [] });
        }
    }

    private handleAssociationChange(e: CustomEvent): void {
        const value = e.detail?.value;
        if (!value) return;

        if (value.includes(SUFFIXES.EVENT)) {
            this.setState({ selectedSurface: SURFACE_TYPES.EVENT, flashbar: [] });
        } else if (value.includes(SUFFIXES.ASIN)) {
            this.setState({ selectedSurface: SURFACE_TYPES.ASIN, flashbar: [] });
        }
    }

    // CRUD operations
    private createPoll = async (): Promise<void> => {
        if (!this.validatePollFields('creating')) return;

        this.setState({ saving: true });

        try {
            const poll = await fetchCreatePoll(this.state.poll);
            this.handleCreateSuccess(poll);
        } catch (error) {
            this.setState({ flashbar: [itemError('Failed to create poll', error)] });
        } finally {
            this.setState({ saving: false });
        }
    };

    private handleCreateSuccess(poll: Poll): void {
        const locale = poll.locale || '';
        this.setState({
            selectedLocale: locale,
            flashbar: [
                itemSuccess(`Poll ${poll.id} with locale ${locale} created successfully`).withButton(
                    'View Poll',
                    () => {
                        this.props.history.push(`/fan-polls/${poll.id}?locale=${locale}`);
                        this.setState({
                            selectedLocale: locale,
                            isEdit: true,
                            pollLocales: [...this.state.pollLocales, locale],
                            flashbar: [],
                        });
                    },
                ),
            ],
        });
    }

    private savePoll = async (): Promise<void> => {
        if (!this.validatePollFields('saving')) return;

        this.setState({ saving: true, showSavePopup: false });
        const pollId = this.props.match.params.pollId;

        try {
            await fetchUpdatePoll(pollId, this.state.selectedLocale, this.state.poll);
            const flashbar = [itemSuccess('Poll saved')];
            this.setState({ flashbar });

            try {
                await this.loadPollDetails(this.state.selectedLocale);
            } catch (error) {
                flashbar.push(itemError('Failed to reload poll', error));
                this.setState({ flashbar });
            }
        } catch (error) {
            this.setState({ flashbar: [itemError('Failed to save poll', error)] });
        } finally {
            this.setState({ saving: false });
        }
    };

    private deletePoll = async (): Promise<void> => {
        this.setState({ deleting: true, showDeletePopup: false });
        const pollId = this.props.match.params.pollId;

        try {
            await fetchDeletePoll(pollId, this.state.selectedLocale);
            this.setState({ poll: {} });
            this.props.history.push('/fan-polls/', { reload: true });
        } catch (error) {
            this.setState({ flashbar: [itemError('Failed to delete poll', error)] });
        } finally {
            this.setState({ deleting: false });
        }
    };

    // Update methods
    private updatePoll = (poll: Poll): void => {
        this.setState({ poll: deepCopy(poll) });
    };

    private updatePollSchedule = (schedule: PollSchedule): void => {
        const updatePollSchedule = {
            start_date: schedule.startTime,
            expiration_date: schedule.endTime,
        };
        const updatePollEligibility = {
            schedule: updatePollSchedule,
        };
        const updatePoll: Poll = {
            ...this.state.poll,
            eligibility: updatePollEligibility,
        };
        this.updatePoll(updatePoll);
    };

    private updateDeletePopup = (shouldDisplayDeletePopup: boolean): void => {
        this.setState({ showDeletePopup: shouldDisplayDeletePopup });
    };

    private updateSavePopup = (shouldDisplaySavePopup: boolean): void => {
        this.setState({ showSavePopup: shouldDisplaySavePopup });
    };

    // Render helpers
    private getHeaderButtons(): PageHeaderButton[] {
        const buttons: PageHeaderButton[] = [];

        if (this.state.isEdit) {
            buttons.push(
                {
                    text: 'Locale',
                    icon: 'add-plus',
                    disabled: this.state.pollLocales.length === 15,
                    loading: this.state.loading,
                    onClick: () =>
                        this.setState({
                            isEdit: false,
                            poll: { ...this.state.poll, locale: undefined },
                        }),
                },
                {
                    text: 'Save',
                    icon: 'upload',
                    disabled: this.state.deleting,
                    loading: this.state.saving,
                    variant: 'primary',
                    onClick: () => this.setState({ showSavePopup: true }),
                },
            );
        } else {
            buttons.push({
                text: 'Create',
                loading: this.state.saving,
                variant: 'primary',
                onClick: this.createPoll,
            });
        }

        buttons.unshift({
            text: 'Delete',
            disabled: this.state.saving,
            loading: this.state.deleting,
            onClick: () => this.setState({ showDeletePopup: true }),
        });

        return buttons;
    }

    private renderLocaleSelect(): JSX.Element {
        const responsiveLabel = 'col-xxxs-1 col-xxs-1';
        const responsiveField = 'col-xxxs-1 col-xxs-1';

        return (
            <>
                <h4 className={responsiveLabel}>Locale</h4>
                <Select
                    className={responsiveField}
                    onChange={this.handleLocaleChange}
                    options={getLocaleOptions(available_locale_options, this.state.pollLocales)}
                    selectedLabel="Selected"
                    selectedOption={getSelection(available_locale_options, this.state.selectedLocale)}
                    id="locale"
                />
            </>
        );
    }

    render(): JSX.Element {
        const headerText = this.state.isEdit ? `Edit poll: ${this.state.poll?.id}` : 'Create poll';
        const headerButtons = this.getHeaderButtons();

        return (
            <div className="awsui-grid">
                <Flashbar items={this.state.flashbar} />
                <FanPollModal
                    showDeletePopup={this.state.showDeletePopup}
                    showSavePopup={this.state.showSavePopup}
                    updateDeletePopup={this.updateDeletePopup}
                    updateSavePopup={this.updateSavePopup}
                    deletePoll={this.deletePoll}
                    savePoll={this.savePoll}
                />
                {this.state.loading ? (
                    <span className="awsui-util-status-inactive">
                        <Spinner /> Loading
                    </span>
                ) : (
                    this.state.poll && (
                        <div>
                            <PageHeader text={headerText} buttons={headerButtons} />
                            {this.state.isEdit && this.renderLocaleSelect()}
                            <div className="empty-space" style={{ height: '40px' }} />
                            <FanPollDataTab
                                poll={this.state.poll}
                                isEdit={this.state.isEdit}
                                onChange={this.handleChange}
                                onPollChanged={this.updatePoll}
                                onPollScheduleChanged={this.updatePollSchedule}
                                scheduleValidator={pollValidation}
                            />
                        </div>
                    )
                )}
            </div>
        );
    }
}

export default EditPoll;
